/**
 * RULE CHANGES (EXPERT-NETWORKS.md §12) — an expert room adjusts the rules it works under. Say what should change →
 * the room's planner proposes a diff → keep the changes you want and apply: TIGHTER ones apply at once, LOOSER ones
 * wait for the approving room (the team's Lead in a deployment) — never approved by the person who proposed them.
 * A change this room made can be proposed to the network's rules (the next release ships it everywhere).
 * Additive section of the room settings; nothing in the Tasks tab moves.
 */
import React, { useState } from 'react';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const KIND = { add: 'New rule', edit: 'Change', remove: 'Remove' };
const STATE = {
  proposed: ['Proposed', 'bg-gray-100 text-gray-700'],
  applied: ['Applied', 'bg-emerald-50 text-emerald-700'],
  pending: ['Waiting for approval', 'bg-amber-50 text-amber-800'],
  declined: ['Declined', 'bg-rose-50 text-rose-700'],
  skipped: ['Skipped', 'bg-gray-50 text-gray-500'],
};
const errText = (e) => (e && e.data && e.data.message) || (e && e.message) || "Couldn't do that.";

function ChangeLine({ c }) {
  const rule = c.rule || {};
  const was = c.was || {};
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-gray-800">
        <span className="font-medium">{KIND[c.kind] || c.kind}</span>
        {c.team ? <span className="text-gray-500"> · {c.team}</span> : null}
        <span className={`ml-1.5 rounded px-1 py-px text-[10px] ${c.class === 'tighten'
          ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>
          {c.class === 'tighten' ? 'Tighter' : 'Looser — needs approval'}
        </span>
      </p>
      {c.kind === 'remove'
        ? <p className="mt-0.5 text-[11px] text-gray-600 line-through">{was.statement}</p>
        : <p className="mt-0.5 text-[11px] text-gray-700">{rule.statement}
          {rule.severity ? <span className="ml-1 text-gray-400">({was.severity && was.severity !== rule.severity
            ? `${was.severity} → ${rule.severity}` : rule.severity})</span> : null}</p>}
      {c.reason ? <p className="mt-0.5 text-[11px] italic text-gray-500">{c.reason}</p> : null}
      {c.note ? <p className="mt-0.5 text-[11px] text-rose-600">{c.note}</p> : null}
    </div>
  );
}

export default function RoomRuleChanges({ value, onAct }) {
  const v = value || {};
  const [ask, setAsk] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [skip, setSkip] = useState({});           // adjustmentId → {index: true} the person unticked
  const run = async (key, req) => {
    if (!onAct) return null;
    setBusy(key);
    setErr('');
    try {
      return await onAct(req);
    } catch (e) {
      setErr(errText(e));
      return null;
    } finally {
      setBusy('');
    }
  };
  const propose = async () => {
    const r = await run('propose', { action: 'propose', ask });
    if (r) setAsk('');
  };
  const apply = (a) => {
    const off = skip[a.adjustmentId] || {};
    const keep = a.changes.map((_, i) => i).filter((i) => !off[i]);
    return run(`apply:${a.adjustmentId}`, { action: 'apply', adjustmentId: a.adjustmentId, keep });
  };
  const items = v.adjustments || [];
  return (
    <div className="space-y-2 text-[12px]" data-testid="room-rule-changes">
      <p className="text-[11px] text-gray-500">
        {v.role === 'department' ? `Adjust the ${v.team || 'team'} rules this room works under. `
          : 'Adjust the rules this room works under. '}
        Tighter changes apply at once; looser ones wait for {v.role === 'room' ? 'another person in this room'
          : "the team's Lead room"}.
      </p>
      {v.canPropose ? (
        <div className="flex gap-2">
          <input value={ask} onChange={(e) => setAsk(e.target.value)} data-testid="rule-change-ask"
            placeholder="What should change? e.g. make bucket privacy a hard gate"
            className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-800" />
          <button type="button" disabled={!ask.trim() || !!busy} onClick={propose} data-testid="rule-change-propose"
            className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}>
            {busy === 'propose' ? 'Planning…' : 'Propose'}
          </button>
        </div>
      ) : null}
      {items.map((a) => (
        <div key={a.adjustmentId} className="rounded-md border border-gray-200 px-2 py-1.5"
          data-testid={`rule-adjustment-${a.adjustmentId}`}>
          <p className="text-[11px] text-gray-500">
            {a.summary || a.ask}<span className="text-gray-400"> · {a.byName || a.by}</span>
          </p>
          <ul className="mt-1 space-y-1.5">
            {a.changes.map((c, i) => {
              const [label, tone] = STATE[c.state] || [c.state, 'bg-gray-100 text-gray-700'];
              return (
                <li key={i} className="flex items-start gap-2" data-testid={`rule-change-${a.adjustmentId}-${i}`}>
                  {c.state === 'proposed' ? (
                    <input type="checkbox" className="mt-0.5" checked={!(skip[a.adjustmentId] || {})[i]}
                      data-testid={`rule-change-keep-${a.adjustmentId}-${i}`}
                      onChange={(e) => setSkip((s) => ({ ...s, [a.adjustmentId]: { ...(s[a.adjustmentId] || {}),
                        [i]: !e.target.checked } }))} />
                  ) : null}
                  <ChangeLine c={c} />
                  <span className={`ml-auto shrink-0 rounded px-1 py-px text-[10px] ${tone}`}>{label}</span>
                  {c.state === 'pending' && v.canApprove ? (
                    <span className="flex shrink-0 gap-1">
                      <button type="button" disabled={!!busy} className={`${btn} bg-amber-600 text-white hover:bg-amber-700`}
                        data-testid={`rule-change-approve-${a.adjustmentId}-${i}`}
                        onClick={() => run(`d:${a.adjustmentId}:${i}`, { action: 'approve', adjustmentId: a.adjustmentId, index: i })}>
                        Approve</button>
                      <button type="button" disabled={!!busy} className={`${btn} text-amber-800 hover:bg-amber-100`}
                        data-testid={`rule-change-decline-${a.adjustmentId}-${i}`}
                        onClick={() => run(`d:${a.adjustmentId}:${i}`, { action: 'decline', adjustmentId: a.adjustmentId, index: i })}>
                        Decline</button>
                    </span>
                  ) : null}
                  {c.state === 'applied' && c.taskId && v.canPropose && !c.proposalId ? (
                    <button type="button" disabled={!!busy} className={`${btn} shrink-0 text-blue-700 hover:bg-blue-50`}
                      data-testid={`rule-change-tobase-${a.adjustmentId}-${i}`}
                      title="Suggest this change for the network's rules — the network's owners accept or decline it"
                      onClick={() => run(`b:${a.adjustmentId}:${i}`, { action: 'toBase', taskId: c.taskId, reason: c.reason })}>
                      Suggest to the network</button>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {a.changes.some((c) => c.state === 'proposed') && v.canPropose ? (
            <div className="mt-1.5 flex justify-end">
              <button type="button" disabled={!!busy} onClick={() => apply(a)} data-testid={`rule-apply-${a.adjustmentId}`}
                className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}>
                {busy === `apply:${a.adjustmentId}` ? 'Applying…' : 'Apply the ticked changes'}</button>
            </div>
          ) : null}
        </div>
      ))}
      {!items.length ? <p className="text-[11px] text-gray-400">No rule changes yet.</p> : null}
      {err ? <p className="text-[11px] text-rose-600" data-testid="rule-change-error">{err}</p> : null}
    </div>
  );
}
